import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  Moon,
  Sun,
  User,
  Shield,
  Palette,
  X,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

// Client-side minimum; the backend enforces the full policy (upper/lower/digit/
// special) and returns a specific message we surface on error.
const MIN_PASSWORD_LENGTH = 9;

const Settings = ({ isOpen, onClose, darkMode, onToggleDarkMode }) => {
  const { user, changePassword, logoutAll } = useAuth();

  const [activeTab, setActiveTab] = useState("appearance");

  // Change-password form state.
  const [showPwForm, setShowPwForm] = useState(false);
  const [pwFields, setPwFields] = useState({ current: "", next: "", confirm: "" });
  // pwStatus.state: 'idle' | 'saving' | 'success' | 'error'
  const [pwStatus, setPwStatus] = useState({ state: "idle", message: "" });

  const [signingOut, setSigningOut] = useState(false);

  const tabs = [
    { id: "appearance", label: "Appearance", icon: Palette },
    { id: "account",    label: "Account",    icon: User },
    { id: "security",   label: "Security",   icon: Shield },
  ];

  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);

  // Reset the change-password form each time the modal is closed so a reopen
  // never shows a stale message or a half-filled form.
  useEffect(() => {
    if (!isOpen) {
      setShowPwForm(false);
      setPwFields({ current: "", next: "", confirm: "" });
      setPwStatus({ state: "idle", message: "" });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // ── Theme-aware colour tokens ──────────────────────────────────────────────
  const bg          = darkMode ? '#0A0A0A' : '#FFFFFF';
  const bgSurface   = darkMode ? '#141414' : '#F5F5F5';
  const bgNav       = darkMode ? '#141414' : '#F0F0F0';
  const border      = darkMode ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.10)';
  const borderStrong= darkMode ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.22)';
  const textPrimary = darkMode ? '#FFFFFF' : '#0A0A0A';
  const textMuted   = darkMode ? 'rgba(255,255,255,0.50)' : 'rgba(0,0,0,0.45)';
  const textDim     = darkMode ? 'rgba(255,255,255,0.30)' : 'rgba(0,0,0,0.28)';
  const accentLine  = darkMode
    ? 'linear-gradient(90deg, transparent, rgba(255,255,255,0.20), transparent)'
    : 'linear-gradient(90deg, transparent, rgba(0,0,0,0.10), transparent)';
  const hoverBg     = darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)';
  const activeTabBg = darkMode ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)';
  const activeTabOutline = darkMode ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.15)';
  const iconBg      = darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
  const iconBorder  = darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)';
  const divider     = darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const successColor = darkMode ? 'rgba(120,220,150,0.95)' : 'rgba(30,140,70,0.95)';
  const errorColor   = darkMode ? 'rgba(230,120,120,0.95)' : 'rgba(190,50,50,0.95)';

  // Toggle on-state: invert — dark mode shows white bg+black dot; light shows black bg+white dot
  const toggleOnBg     = darkMode ? '#FFFFFF' : '#0A0A0A';
  const toggleOnBorder = darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)';
  const toggleOnDot    = darkMode ? '#0A0A0A' : '#FFFFFF';
  const toggleOffBg    = darkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)';
  const toggleOffDot   = darkMode ? 'rgba(255,255,255,0.60)' : 'rgba(0,0,0,0.45)';

  const inputStyle = {
    width: '100%', boxSizing: 'border-box',
    background: bgSurface, border: `1px solid ${borderStrong}`,
    borderRadius: 6, color: textPrimary,
    padding: '9px 12px', fontSize: 14, fontFamily: 'Inter, sans-serif',
    outline: 'none',
  };

  // ── Change-password handlers ────────────────────────────────────────────────
  const openPwForm = () => {
    setPwStatus({ state: "idle", message: "" });
    setPwFields({ current: "", next: "", confirm: "" });
    setShowPwForm(true);
  };

  const closePwForm = () => {
    setShowPwForm(false);
    setPwFields({ current: "", next: "", confirm: "" });
    setPwStatus({ state: "idle", message: "" });
  };

  const handleSavePassword = async () => {
    if (!pwFields.current || !pwFields.next) {
      setPwStatus({ state: "error", message: "Please fill in every field." });
      return;
    }
    if (pwFields.next.length < MIN_PASSWORD_LENGTH) {
      setPwStatus({ state: "error", message: `New password must be at least ${MIN_PASSWORD_LENGTH} characters.` });
      return;
    }
    if (pwFields.next !== pwFields.confirm) {
      setPwStatus({ state: "error", message: "New passwords do not match." });
      return;
    }

    setPwStatus({ state: "saving", message: "" });
    const result = await changePassword({
      currentPassword: pwFields.current,
      newPassword: pwFields.next,
    });
    if (result?.success) {
      setPwFields({ current: "", next: "", confirm: "" });
      setShowPwForm(false);
      setPwStatus({ state: "success", message: result.message || "Password changed successfully." });
    } else {
      setPwStatus({ state: "error", message: result?.error || "Failed to change password." });
    }
  };

  const handleSignOutAll = async () => {
    setSigningOut(true);
    // logoutAll revokes every session then runs the local logout, which clears
    // auth state; the app's route guard then redirects to the sign-in page and
    // unmounts this modal, so no explicit navigation is needed here.
    await logoutAll();
  };

  // ── Sub-components (defined inline so they close over theme tokens) ─────────

  const SectionCard = ({ children }) => (
    <div style={{
      background: bgSurface, border: `1px solid ${border}`,
      borderRadius: 4, padding: '20px 24px',
      position: 'relative', overflow: 'hidden',
    }}>
      <div aria-hidden style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 1,
        background: accentLine,
      }} />
      {children}
    </div>
  );

  const RowDivider = () => (
    <div style={{ height: 1, background: divider, margin: '10px 0' }} />
  );

  const ToggleSwitch = ({ checked, onChange }) => (
    <button
      onClick={onChange}
      role="switch"
      aria-checked={checked}
      style={{
        position: 'relative', display: 'inline-flex',
        width: 40, height: 22, borderRadius: 9999, cursor: 'pointer',
        flexShrink: 0, border: 'none',
        background: checked ? toggleOnBg : toggleOffBg,
        outline: `1px solid ${checked ? toggleOnBorder : border}`,
        outlineOffset: -1,
        transition: 'background 200ms',
      }}
    >
      <span style={{
        position: 'absolute', top: 3,
        left: checked ? 'calc(100% - 19px)' : 3,
        width: 16, height: 16, borderRadius: 9999,
        background: checked ? toggleOnDot : toggleOffDot,
        transition: 'left 200ms',
      }} />
    </button>
  );

  const PillButton = ({ children, onClick, variant = 'ghost', disabled = false }) => {
    const base = {
      padding: '7px 16px', borderRadius: 9999,
      fontFamily: 'Inter, sans-serif', fontSize: 14,
      cursor: disabled ? 'default' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      display: 'inline-flex', alignItems: 'center', gap: 6,
      transition: 'border-color 150ms, color 150ms, background 150ms',
    };
    if (variant === 'ghost') return (
      <button
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        style={{ ...base, border: `1px solid ${borderStrong}`, background: 'transparent', color: textMuted }}
        onMouseEnter={(e) => { if (!disabled) { e.currentTarget.style.borderColor = textPrimary; e.currentTarget.style.color = textPrimary; } }}
        onMouseLeave={(e) => { if (!disabled) { e.currentTarget.style.borderColor = borderStrong; e.currentTarget.style.color = textMuted; } }}
      >{children}</button>
    );
    if (variant === 'solid') return (
      <button
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        style={{ ...base, border: `1px solid ${toggleOnBorder}`, background: toggleOnBg, color: toggleOnDot }}
      >{children}</button>
    );
    if (variant === 'danger') return (
      <button
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        style={{ ...base, border: '1px solid rgba(200,50,50,0.40)', background: 'transparent', color: 'rgba(200,80,80,0.90)' }}
        onMouseEnter={(e) => { if (!disabled) { e.currentTarget.style.borderColor = 'rgba(220,50,50,0.80)'; e.currentTarget.style.color = 'rgba(230,80,80,1)'; } }}
        onMouseLeave={(e) => { if (!disabled) { e.currentTarget.style.borderColor = 'rgba(200,50,50,0.40)'; e.currentTarget.style.color = 'rgba(200,80,80,0.90)'; } }}
      >{children}</button>
    );
    return null;
  };

  const SectionHeader = ({ title }) => (
    <div style={{ marginBottom: 20 }}>
      <h3 className="font-display" style={{
        fontSize: '1.125rem', fontWeight: 400, letterSpacing: '-0.01em',
        color: textPrimary, margin: 0,
      }}>{title}</h3>
    </div>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.55)',
        zIndex: 2147483000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
      }}
    >
      <div style={{
        width: '100%', maxWidth: 760, height: '78vh',
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: 4,
        boxShadow: darkMode ? '0 32px 80px rgba(0,0,0,0.8)' : '0 32px 80px rgba(0,0,0,0.18)',
        overflow: 'hidden', display: 'flex', flexDirection: 'column',
        position: 'relative',
      }}>
        {/* Top accent line */}
        <div aria-hidden style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 1,
          background: accentLine,
        }} />

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
          padding: '20px 24px 18px',
          borderBottom: `1px solid ${border}`,
          flexShrink: 0,
        }}>
          <div>
            <h2 className="font-display" style={{
              fontSize: '1.625rem', fontWeight: 400,
              lineHeight: 1.1, letterSpacing: '-0.02em',
              color: textPrimary, margin: 0,
            }}>Settings</h2>
          </div>
          <button
            onClick={onClose}
            style={{
              padding: '6px 14px', borderRadius: 9999,
              border: `1px solid ${borderStrong}`, background: 'transparent',
              color: textMuted, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 14, fontFamily: 'Inter, sans-serif',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = textPrimary; e.currentTarget.style.color = textPrimary; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = borderStrong; e.currentTarget.style.color = textMuted; }}
          >
            <X size={14} /> Close
          </button>
        </div>

        {/* Body */}
        <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
          {/* Left nav */}
          <div style={{
            width: 200, flexShrink: 0,
            borderRight: `1px solid ${border}`,
            background: bgNav,
            padding: '16px 12px', overflowY: 'auto',
          }}>
            <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    style={{
                      width: '100%', textAlign: 'left',
                      padding: '7px 10px', borderRadius: 6, border: 'none',
                      background: isActive ? activeTabBg : 'transparent',
                      color: isActive ? textPrimary : textMuted,
                      opacity: isActive ? 1 : 0.9,
                      cursor: 'pointer', fontSize: 14, fontWeight: isActive ? 500 : 400,
                      fontFamily: 'Inter, sans-serif',
                      display: 'flex', alignItems: 'center', gap: 10,
                      outline: isActive ? `1px solid ${activeTabOutline}` : 'none',
                      outlineOffset: -1,
                      transition: 'background 150ms, color 150ms',
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) { e.currentTarget.style.background = hoverBg; e.currentTarget.style.color = textPrimary; }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = textMuted; }
                    }}
                  >
                    <Icon size={15} />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Content */}
          <div style={{
            flex: 1, padding: '24px',
            overflowY: 'auto', fontFamily: 'Inter, sans-serif',
          }}>

            {activeTab === "appearance" && (
              <div>
                <SectionHeader title="Appearance" />
                <SectionCard>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: 6,
                        background: iconBg, border: `1px solid ${iconBorder}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {darkMode
                          ? <Moon size={16} style={{ color: textMuted }} />
                          : <Sun  size={16} style={{ color: textMuted }} />}
                      </div>
                      <div>
                        <div style={{ fontSize: 15, fontWeight: 500, color: textPrimary }}>
                          {darkMode ? "Dark mode" : "Light mode"}
                        </div>
                        <div style={{ fontSize: 13, color: textMuted, marginTop: 2 }}>
                          {darkMode
                            ? "Reduced eye strain in low light"
                            : "Higher contrast for bright environments"}
                        </div>
                      </div>
                    </div>
                    <ToggleSwitch checked={darkMode} onChange={onToggleDarkMode} />
                  </div>
                </SectionCard>
              </div>
            )}

            {activeTab === "account" && (
              <div>
                <SectionHeader title="Account" />
                <SectionCard>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
                    <div style={{
                      width: 48, height: 48, borderRadius: 9999,
                      background: iconBg, border: `1px solid ${border}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 19, fontWeight: 600, color: textPrimary,
                    }}>
                      {user?.full_name?.charAt(0) || user?.username?.charAt(0) || 'U'}
                    </div>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 500, color: textPrimary }}>
                        {user?.full_name || 'User'}
                      </div>
                      <div style={{ fontSize: 13, color: textMuted, marginTop: 2 }}>
                        {user?.email || 'No email'}
                      </div>
                      <div style={{ fontSize: 12, color: textDim, marginTop: 1 }}>
                        @{user?.username || 'username'}
                      </div>
                    </div>
                  </div>
                  <RowDivider />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0' }}>
                      <span style={{ fontSize: 14, color: textPrimary }}>Change password</span>
                      {showPwForm
                        ? <PillButton variant="ghost" onClick={closePwForm}>Cancel</PillButton>
                        : <PillButton variant="ghost" onClick={openPwForm}>Update</PillButton>}
                    </div>

                    {showPwForm && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '8px 0 4px' }}>
                        <input
                          type="password"
                          autoComplete="current-password"
                          placeholder="Current password"
                          value={pwFields.current}
                          onChange={(e) => setPwFields((p) => ({ ...p, current: e.target.value }))}
                          style={inputStyle}
                        />
                        <input
                          type="password"
                          autoComplete="new-password"
                          placeholder="New password"
                          value={pwFields.next}
                          onChange={(e) => setPwFields((p) => ({ ...p, next: e.target.value }))}
                          style={inputStyle}
                        />
                        <input
                          type="password"
                          autoComplete="new-password"
                          placeholder="Confirm new password"
                          value={pwFields.confirm}
                          onChange={(e) => setPwFields((p) => ({ ...p, confirm: e.target.value }))}
                          style={inputStyle}
                        />
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <PillButton variant="solid" onClick={handleSavePassword} disabled={pwStatus.state === 'saving'}>
                            {pwStatus.state === 'saving' ? 'Saving…' : 'Save new password'}
                          </PillButton>
                        </div>
                      </div>
                    )}

                    {pwStatus.message && (
                      <div style={{
                        fontSize: 13, marginTop: 4,
                        color: pwStatus.state === 'success' ? successColor : errorColor,
                      }}>
                        {pwStatus.message}
                      </div>
                    )}

                    <RowDivider />
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0' }}>
                      <span style={{ fontSize: 14, color: textPrimary }}>Email address</span>
                      <span style={{ fontSize: 14, color: textMuted }}>{user?.email || 'Not set'}</span>
                    </div>
                  </div>
                </SectionCard>
              </div>
            )}

            {activeTab === "security" && (
              <div>
                <SectionHeader title="Security" />
                <SectionCard>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 500, color: textPrimary }}>
                        Sign out of all devices
                      </div>
                      <div style={{ fontSize: 13, color: textMuted, marginTop: 3 }}>
                        Terminates all active sessions across browsers and devices
                      </div>
                    </div>
                    <PillButton variant="danger" onClick={handleSignOutAll} disabled={signingOut}>
                      {signingOut ? 'Signing out…' : 'Sign out all'}
                    </PillButton>
                  </div>
                </SectionCard>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default Settings;
